<script lang="ts">
	import { Button, Text, Toggle } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig, VentoraLightConfig } from 'src/types/ventora_fan';
	import VentoraFanNumericInput from './VentoraFanNumericInput.svelte';
	import {
		VENTORA_LIGHT_OFF_DELAY_HINT,
		VENTORA_LIGHT_ON_DELAY_HINT,
		VENTORA_LIGHT_SPEED_HINT,
		VENTORA_LIGHT_TRIGGER_PRIORITY_HINT,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraClamp,
		ventoraFiniteNumberFromInput,
		ventoraHasValidationErrors,
		ventoraLightLimits,
		ventoraNumericValidation,
		ventoraToNumber,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	type LightNumericField = keyof Pick<VentoraLightConfig, 'speed' | 'on_delay' | 'off_delay'>;

	let loading = false;
	let dirty = false;
	let draft: VentoraLightConfig = { ...defaultVentoraFanConfig.light };

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draft = { ...config.light };
	}

	$: speedValidation = ventoraNumericValidation(draft.speed, ...ventoraLightLimits.speed);
	$: onDelayValidation = ventoraNumericValidation(draft.on_delay, ...ventoraLightLimits.on_delay);
	$: offDelayValidation = ventoraNumericValidation(draft.off_delay, ...ventoraLightLimits.off_delay);
	$: hasValidationErrors = ventoraHasValidationErrors([
		speedValidation,
		onDelayValidation,
		offDelayValidation,
	]);

	const inputField = (field: LightNumericField, e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draft = { ...draft, [field]: n };
	};

	const blurField = (field: LightNumericField, e: Event) => {
		const [min, max] = ventoraLightLimits[field];
		const raw = (e.currentTarget as HTMLInputElement).value;
		draft = {
			...draft,
			[field]: ventoraClamp(ventoraToNumber(raw, draft[field]), min, max),
		};
	};

	const commitLightDraft = () => {
		draft = {
			...draft,
			speed: ventoraClamp(draft.speed, ...ventoraLightLimits.speed),
			on_delay: ventoraClamp(draft.on_delay, ...ventoraLightLimits.on_delay),
			off_delay: ventoraClamp(draft.off_delay, ...ventoraLightLimits.off_delay),
		};
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		commitLightDraft();
		loading = true;
		const ok = await patchVentoraFanConfig(device, { light: { ...draft } });
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format(VENTORA_LIGHT_TRIGGER_PRIORITY_HINT.key, {
		default: VENTORA_LIGHT_TRIGGER_PRIORITY_HINT.default,
	})}
</Text>

<Toggle
	size="m"
	class="mb-m"
	checked={draft.enable}
	on:click={() => {
		draft = { ...draft, enable: !draft.enable };
		dirty = true;
	}}
	id="toggle-ventora-light-trigger"
>
	{$format('ventora_light_enable', { default: 'Enable light trigger' })}
</Toggle>

<VentoraFanNumericInput
	_id="input-ventora-light-speed"
	labelKey="ventora_light_speed"
	labelDefault="Speed"
	hintKey={VENTORA_LIGHT_SPEED_HINT.key}
	hintDefault={VENTORA_LIGHT_SPEED_HINT.default}
	value={draft.speed}
	min={ventoraLightLimits.speed[0]}
	max={ventoraLightLimits.speed[1]}
	icon="fas fa-fan"
	validation={speedValidation}
	onInput={(e) => inputField('speed', e)}
	onBlur={(e) => blurField('speed', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-light-on-delay"
	labelKey="ventora_light_on_delay"
	labelDefault="On delay (seconds)"
	hintKey={VENTORA_LIGHT_ON_DELAY_HINT.key}
	hintDefault={VENTORA_LIGHT_ON_DELAY_HINT.default}
	value={draft.on_delay}
	min={ventoraLightLimits.on_delay[0]}
	max={ventoraLightLimits.on_delay[1]}
	icon="fas fa-timer"
	validation={onDelayValidation}
	onInput={(e) => inputField('on_delay', e)}
	onBlur={(e) => blurField('on_delay', e)}
/>

<VentoraFanNumericInput
	_id="input-ventora-light-off-delay"
	labelKey="ventora_light_off_delay"
	labelDefault="Off delay (seconds)"
	hintKey={VENTORA_LIGHT_OFF_DELAY_HINT.key}
	hintDefault={VENTORA_LIGHT_OFF_DELAY_HINT.default}
	value={draft.off_delay}
	min={ventoraLightLimits.off_delay[0]}
	max={ventoraLightLimits.off_delay[1]}
	icon="fas fa-hourglass-end"
	validation={offDelayValidation}
	onInput={(e) => inputField('off_delay', e)}
	onBlur={(e) => blurField('off_delay', e)}
/>

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-light-trigger-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
